/**
 * Form 2: Market & Business Model
 * 
 * Collects market research and financial projections for pitch deck generation.
 * Includes radio buttons, conditional fields, and validation.
 */

import { useState, useCallback } from "react";
import { Check, AlertCircle, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import {
  Form2Data,
  countWords,
  validateWordCount,
  validateRequired,
  validateProfitMargin,
  validateCompetitors,
  validateForm2,
} from "@/hooks/usePitchDeckForm";

interface PitchDeckForm2Props {
  formData: Form2Data;
  updateField: <K extends keyof Form2Data>(field: K, value: Form2Data[K]) => void;
  errors: Partial<Record<keyof Form2Data, string>>;
  setErrors: React.Dispatch<React.SetStateAction<Partial<Record<string, string>>>>;
  onSave: (showMessage?: boolean, markForm1Complete?: boolean, markForm2Complete?: boolean) => Promise<boolean>;
  onBack: () => void;
  onComplete?: () => void;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

interface FormFieldProps {
  label: string;
  error?: string;
  isValid?: boolean;
  required?: boolean;
  children: React.ReactNode;
  helperText?: React.ReactNode;
}

function FormField({ label, error, isValid, required, children, helperText }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label className="font-semibold">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {isValid && !error && (
          <Check className="h-4 w-4 text-green-600" aria-label="Valid" />
        )}
      </div>
      {children}
      {helperText && (
        <div className="text-sm text-muted-foreground">{helperText}</div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );
}

function WordCountIndicator({ text, min, max }: { text: string; min: number; max: number }) {
  const count = countWords(text);
  const isValid = count >= min && count <= max;
  const isEmpty = count === 0;

  return (
    <span
      className={cn(
        "text-sm",
        isEmpty && "text-muted-foreground",
        !isEmpty && isValid && "text-green-600",
        !isEmpty && !isValid && "text-destructive"
      )}
    >
      Word count: {count}/{max}
    </span>
  );
}

// Market size options
const LOCAL_MARKET_OPTIONS = [
  { value: "less_than_10", label: "Less than 10 potential customers" },
  { value: "at_least_100", label: "At least 100 potential customers" },
  { value: "up_to_1000", label: "Up to 1,000 potential customers" },
  { value: "1000_plus", label: "1,000+ potential customers" },
];

const REGIONAL_MARKET_OPTIONS = [
  { value: "1_100", label: "1-100 potential customers" },
  { value: "101_1000", label: "101-1,000 potential customers" },
  { value: "1001_plus", label: "1,001+ potential customers" },
];

const COMPETITION_OPTIONS = [
  { value: "no_competition", label: "No competition" },
  { value: "several_competitors", label: "Several competitors" },
  { value: "other", label: "Other" },
];

const SALES_PROJECTION_OPTIONS = [
  { value: "1_100k", label: "$1 - $100,000" },
  { value: "100k_1m", label: "$100,000 - $1,000,000" },
  { value: "1m_plus", label: "$1,000,000+" },
];

export function PitchDeckForm2({
  formData,
  updateField,
  errors,
  setErrors,
  onSave,
  onBack,
  onComplete,
  isSaving,
  lastSaved,
  hasUnsavedChanges,
}: PitchDeckForm2Props) {
  const [touched, setTouched] = useState<Partial<Record<keyof Form2Data, boolean>>>({});

  const handleBlur = useCallback((field: keyof Form2Data) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    let error: string | null = null;
    
    switch (field) {
      case "product_description":
        error = validateWordCount(formData.product_description, "Product description", 10, 50);
        break;
      case "market_size_local":
        error = validateRequired(formData.market_size_local, "your local market size");
        break;
      case "market_size_regional":
        error = validateRequired(formData.market_size_regional, "your regional market size");
        break;
      case "competitors":
      case "competitors_other":
        error = validateCompetitors(formData.competitors, formData.competitors_other);
        break;
      case "differentiation":
        error = validateWordCount(formData.differentiation, "Differentiation", 10, 50);
        break;
      case "sales_projection":
        error = validateRequired(formData.sales_projection, "your annual sales projection");
        break;
      case "profit_margin":
        error = validateProfitMargin(formData.profit_margin);
        break;
    }
    
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  }, [formData, setErrors]);

  const isFieldValid = (field: keyof Form2Data): boolean => {
    if (!touched[field]) return false;
    return !errors[field] && (
      field === "profit_margin" 
        ? formData[field] !== null 
        : !!formData[field]
    );
  };

  const handleSaveDraft = async () => {
    const validationErrors = validateForm2(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({
        product_description: true,
        market_size_local: true,
        market_size_regional: true,
        competitors: true,
        competitors_other: true,
        differentiation: true,
        sales_projection: true,
        profit_margin: true,
      });
      return;
    }
    await onSave(true, false, false);
  };

  const handleComplete = async () => {
    const validationErrors = validateForm2(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({
        product_description: true,
        market_size_local: true,
        market_size_regional: true,
        competitors: true,
        competitors_other: true,
        differentiation: true,
        sales_projection: true,
        profit_margin: true,
      });
      return;
    }

    // Save and mark form 2 as complete (don't show the draft message)
    const success = await onSave(false, false, true);
    
    // If save was successful, trigger the complete callback
    if (success && onComplete) {
      onComplete();
    }
  };

  const formatLastSaved = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const isFormValid = Object.keys(validateForm2(formData)).length === 0;

  const handleCompetitorsChange = (value: string) => {
    updateField("competitors", value);
    if (value !== "other") {
      updateField("competitors_other", "");
    }
    setTouched((prev) => ({ ...prev, competitors: true }));
    
    // Validate with the NEW value directly, not from stale formData
    const error = validateCompetitors(value, value === "other" ? formData.competitors_other : "");
    setErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next.competitors = error;
      } else {
        delete next.competitors;
      }
      return next;
    });
  };

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      {/* Product/Service Description Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Product Information</h3>

        <FormField
          label="Product/Service Description"
          error={touched.product_description ? errors.product_description : undefined}
          isValid={isFieldValid("product_description")}
          required
          helperText={
            <WordCountIndicator text={formData.product_description} min={10} max={50} />
          }
        >
          <Textarea
            placeholder="Describe your product or service in detail (10-50 words)"
            value={formData.product_description}
            onChange={(e) => updateField("product_description", e.target.value)}
            onBlur={() => handleBlur("product_description")}
            rows={4}
            className={cn(
              touched.product_description && errors.product_description && "border-destructive",
              isFieldValid("product_description") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Market Size Section */}
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-lg font-semibold text-foreground">Market Size</h3>

        <FormField
          label="Local Market Size"
          error={touched.market_size_local ? errors.market_size_local : undefined}
          isValid={isFieldValid("market_size_local")}
          required
        >
          <RadioGroup
            value={formData.market_size_local}
            onValueChange={(value) => {
              updateField("market_size_local", value);
              setTouched((prev) => ({ ...prev, market_size_local: true }));
              // Clear error immediately when valid selection made
              setErrors((prev) => {
                const next = { ...prev };
                delete next.market_size_local;
                return next;
              });
            }}
            className="space-y-2"
          >
            {LOCAL_MARKET_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors",
                  "hover:bg-muted/50",
                  formData.market_size_local === option.value && "border-primary bg-primary/5"
                )}
              >
                <RadioGroupItem value={option.value} />
                <span className="text-foreground">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        </FormField>

        <FormField
          label="Regional Market Size"
          error={touched.market_size_regional ? errors.market_size_regional : undefined}
          isValid={isFieldValid("market_size_regional")}
          required
        >
          <RadioGroup
            value={formData.market_size_regional}
            onValueChange={(value) => {
              updateField("market_size_regional", value);
              setTouched((prev) => ({ ...prev, market_size_regional: true }));
              // Clear error immediately when valid selection made
              setErrors((prev) => {
                const next = { ...prev };
                delete next.market_size_regional;
                return next;
              });
            }}
            className="space-y-2"
          >
            {REGIONAL_MARKET_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors",
                  "hover:bg-muted/50",
                  formData.market_size_regional === option.value && "border-primary bg-primary/5"
                )}
              >
                <RadioGroupItem value={option.value} />
                <span className="text-foreground">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        </FormField>
      </div>

      {/* Competition Section */}
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-lg font-semibold text-foreground">Competition</h3>

        <FormField
          label="Competition"
          error={touched.competitors ? errors.competitors : undefined}
          isValid={isFieldValid("competitors")}
          required
        >
          <RadioGroup
            value={formData.competitors}
            onValueChange={handleCompetitorsChange}
            className="space-y-2"
          >
            {COMPETITION_OPTIONS.map((option) => (
              <div key={option.value}>
                <label
                  className={cn(
                    "flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors",
                    "hover:bg-muted/50",
                    formData.competitors === option.value && "border-primary bg-primary/5"
                  )}
                >
                  <RadioGroupItem value={option.value} />
                  <span className="text-foreground">{option.label}</span>
                </label>
                {/* Conditional "Other" text input */}
                {option.value === "other" && formData.competitors === "other" && (
                  <div className="ml-8 mt-2">
                    <Input
                      placeholder="Describe your competition"
                      value={formData.competitors_other}
                      onChange={(e) => updateField("competitors_other", e.target.value)}
                      onBlur={() => handleBlur("competitors_other")}
                      className={cn(
                        touched.competitors_other && 
                        errors.competitors && 
                        formData.competitors === "other" && 
                        "border-destructive"
                      )}
                    />
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>
        </FormField>

        <FormField
          label="What Makes You Different?"
          error={touched.differentiation ? errors.differentiation : undefined}
          isValid={isFieldValid("differentiation")}
          required
          helperText={
            <WordCountIndicator text={formData.differentiation} min={10} max={50} />
          }
        >
          <Textarea
            placeholder="Explain your competitive advantage (10-50 words)"
            value={formData.differentiation}
            onChange={(e) => updateField("differentiation", e.target.value)}
            onBlur={() => handleBlur("differentiation")}
            rows={4}
            className={cn(
              touched.differentiation && errors.differentiation && "border-destructive",
              isFieldValid("differentiation") && "border-green-500"
            )}
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Financial Section */}
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-lg font-semibold text-foreground">Financial Projections</h3>

        <FormField
          label="Annual Sales Projection"
          error={touched.sales_projection ? errors.sales_projection : undefined}
          isValid={isFieldValid("sales_projection")}
          required
        >
          <RadioGroup
            value={formData.sales_projection}
            onValueChange={(value) => {
              updateField("sales_projection", value);
              setTouched((prev) => ({ ...prev, sales_projection: true }));
              // Clear error immediately when valid selection made
              setErrors((prev) => {
                const next = { ...prev };
                delete next.sales_projection;
                return next;
              });
            }}
            className="space-y-2"
          >
            {SALES_PROJECTION_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors",
                  "hover:bg-muted/50",
                  formData.sales_projection === option.value && "border-primary bg-primary/5"
                )}
              >
                <RadioGroupItem value={option.value} />
                <span className="text-foreground">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        </FormField>

        <FormField
          label="Expected Profit Margin"
          error={touched.profit_margin ? errors.profit_margin : undefined}
          isValid={isFieldValid("profit_margin")}
          required
          helperText="Enter percentage (e.g., 25 for 25%)"
        >
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={100}
              step={0.1}
              placeholder="25"
              value={formData.profit_margin === null ? "" : String(formData.profit_margin)}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Handle empty string as null, otherwise parse as float
                if (inputValue === "") {
                  updateField("profit_margin", null);
                } else {
                  const numValue = parseFloat(inputValue);
                  // Only update if it's a valid number
                  if (!isNaN(numValue)) {
                    updateField("profit_margin", numValue);
                  }
                }
              }}
              onBlur={() => handleBlur("profit_margin")}
              className={cn(
                "w-24",
                touched.profit_margin && errors.profit_margin && "border-destructive",
                isFieldValid("profit_margin") && "border-green-500"
              )}
              aria-label="Profit margin percentage"
              aria-required="true"
            />
            <span className="text-foreground font-medium">%</span>
          </div>
        </FormField>
      </div>

      {/* Save Status & Actions */}
      <div className="pt-6 border-t space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>
            {isSaving ? (
              <span>Saving...</span>
            ) : lastSaved ? (
              <span>Last saved at {formatLastSaved(lastSaved)}</span>
            ) : hasUnsavedChanges ? (
              <span className="text-warning">Unsaved changes</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-4 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Form 1
          </Button>
          
          <div className="flex flex-col-reverse sm:flex-row gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isSaving}
            >
              Save Draft
            </Button>
            <Button
              type="button"
              onClick={handleComplete}
              disabled={isSaving || !isFormValid}
              className="sm:min-w-[180px]"
            >
              Complete Forms
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
